import * as React from "react";
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery } from "@apollo/client";
import { SUBMIT_AGENT_APPLICATION } from "../../../../platform/graphql/mutations/user";
import { GET_ALL_COUNTRIES } from "../../../../platform/graphql/queries/countryStateCity";

export default function AgentApplicationPage() {
  const locationQuery = useQuery(GET_ALL_COUNTRIES, {
    fetchPolicy: "cache-first",
  });

  const countries = React.useMemo(
    () => locationQuery.data?.getAllCountries ?? [],
    [locationQuery.data],
  );

  const [form, setForm] = React.useState({
    username: "",
    email: "",
    phone: "",
    address: "",
    country: "",
    state: "",
    city: "",
    zipcode: "",
    serviceAreas: "",
    specialties: "",
    notes: "",
  });
  const [status, setStatus] = React.useState<{ type: "success" | "error"; text: string } | null>(
    null,
  );

  const selectedCountry = React.useMemo(
    () => countries.find((item: any) => item.name === form.country) || null,
    [countries, form.country],
  );
  const states = React.useMemo(() => selectedCountry?.states || [], [selectedCountry]);
  const selectedState = React.useMemo(
    () => states.find((item: any) => item.name === form.state) || null,
    [states, form.state],
  );
  const cities = React.useMemo(() => selectedState?.cities || [], [selectedState]);

  const [submitApplication, { loading }] = useMutation(SUBMIT_AGENT_APPLICATION, {
    onCompleted: () => {
      setStatus({
        type: "success",
        text: "Application submitted. We will review it before enabling agent access.",
      });
      setForm({
        username: "",
        email: "",
        phone: "",
        address: "",
        country: "",
        state: "",
        city: "",
        zipcode: "",
        serviceAreas: "",
        specialties: "",
        notes: "",
      });
    },
    onError: (error) => {
      setStatus({
        type: "error",
        text: error.message || "Could not submit your application.",
      });
    },
  });

  const updateField = (key: string, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setStatus(null);
    await submitApplication({
      variables: {
        ...form,
        phone: form.phone.trim() || null,
        address: form.address.trim() || null,
        country: form.country.trim() || null,
        state: form.state.trim() || null,
        city: form.city.trim() || null,
        zipcode: form.zipcode.trim() || null,
        serviceAreas: form.serviceAreas.trim() || null,
        specialties: form.specialties.trim() || null,
        notes: form.notes.trim() || null,
      },
    });
  };

  return (
    <Box sx={{ px: 2, py: { xs: 3, md: 5 }, display: "flex", justifyContent: "center" }}>
      <Paper variant="outlined" sx={{ width: "100%", maxWidth: 860, p: { xs: 2, md: 3 }, borderRadius: 4 }}>
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              Apply to Become an Agent
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              Submit your profile and service area. An admin will review your application before
              agent access is approved.
            </Typography>
          </Box>

          {status ? <Alert severity={status.type}>{status.text}</Alert> : null}

          <Alert severity="info">
            Approval is required. You will not receive agent workspace access until an admin
            reviews and approves your application.
          </Alert>

          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  label="Full name"
                  value={form.username}
                  onChange={(event) => updateField("username", event.target.value)}
                  required
                  fullWidth
                />
                <TextField
                  label="Email"
                  type="email"
                  value={form.email}
                  onChange={(event) => updateField("email", event.target.value)}
                  required
                  fullWidth
                />
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  label="Phone"
                  value={form.phone}
                  onChange={(event) => updateField("phone", event.target.value)}
                  fullWidth
                />
                <TextField
                  label="Address"
                  value={form.address}
                  onChange={(event) => updateField("address", event.target.value)}
                  fullWidth
                />
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  select
                  label="Country"
                  value={form.country}
                  onChange={(event) => {
                    updateField("country", event.target.value);
                    updateField("state", "");
                    updateField("city", "");
                  }}
                  disabled={locationQuery.loading}
                  fullWidth
                >
                  {countries.map((item: any) => (
                    <MenuItem key={item.id} value={item.name}>
                      {item.name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="State"
                  value={form.state}
                  onChange={(event) => {
                    updateField("state", event.target.value);
                    updateField("city", "");
                  }}
                  disabled={!form.country || locationQuery.loading}
                  helperText={!form.country ? "Select country first" : " "}
                  fullWidth
                >
                  {states.map((item: any) => (
                    <MenuItem key={item.id} value={item.name}>
                      {item.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  select
                  label="City"
                  value={form.city}
                  onChange={(event) => updateField("city", event.target.value)}
                  disabled={!form.country || !form.state || locationQuery.loading}
                  helperText={!form.state ? "Select state first" : " "}
                  fullWidth
                >
                  {cities.map((item: any) => (
                    <MenuItem key={item.id} value={item.name}>
                      {item.name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Zip code"
                  value={form.zipcode}
                  onChange={(event) => updateField("zipcode", event.target.value)}
                  fullWidth
                />
              </Stack>

              <TextField
                label="Service areas"
                value={form.serviceAreas}
                onChange={(event) => updateField("serviceAreas", event.target.value)}
                helperText="Example: Spokane, Spokane Valley, North Idaho"
                fullWidth
              />
              <TextField
                label="Specialties"
                value={form.specialties}
                onChange={(event) => updateField("specialties", event.target.value)}
                helperText="Example: AFH placement, memory care, private pay, hospital discharge"
                fullWidth
              />
              <TextField
                label="Why do you want to become an agent?"
                value={form.notes}
                onChange={(event) => updateField("notes", event.target.value)}
                multiline
                minRows={4}
                fullWidth
              />

              <Stack direction="row" justifyContent="flex-end">
                <Button type="submit" variant="contained" disabled={loading}>
                  Submit application
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
}
